import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, X, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/lib/api';
import { PLAN_PRICING, type PricingPlan } from '@/lib/pricing';

interface StripePaymentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amount?: number;
  planName?: string;
  planDays?: number;
}

const API_BASE_URL = getApiUrl();

export function StripePayment({
  open,
  onOpenChange,
  amount = PLAN_PRICING.fundraise.price,
  planName = PLAN_PRICING.fundraise.name,
}: StripePaymentProps) {
  const { isAuthenticated, user, token } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);

  const activePlan: PricingPlan =
    (Object.values(PLAN_PRICING) as PricingPlan[]).find((plan) => plan.name === planName) ??
    PLAN_PRICING.fundraise;
  const normalizedAmount = Number.isFinite(Number(amount)) ? Number(amount) : activePlan.price;
  const displayedFeatures =
    Array.isArray(activePlan.features) && activePlan.features.length > 0
      ? activePlan.features
      : ['Access to selected plan'];

  // Pre-fill email if user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setEmail(isAuthenticated && user?.email ? user.email : '');
        setError(null);
        setHasAccount(false);
        setIsLoading(false);
      }, 300);
    }
  }, [open, isAuthenticated, user]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setError(null);
      onOpenChange(false);
    }, 300);
  };

  const initializeCheckout = async () => {
    // Only validate email for non-authenticated users
    if (!isAuthenticated && (!email || !email.includes('@'))) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasAccount(false);

    try {
      // Use authenticated endpoint if logged in, otherwise use public endpoint
      const endpoint = isAuthenticated 
        ? `${API_BASE_URL}/api/create-checkout-session`
        : `${API_BASE_URL}/api/create-checkout-session-public`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add auth token if authenticated
      if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const body = isAuthenticated
        ? { planName }
        : { email: email.toLowerCase().trim(), planName };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.hasAccount) {
          setHasAccount(true);
          setError(data.message || 'This email is already registered. Please sign in to subscribe.');
        } else {
          setError(data.error || 'Failed to create checkout session');
        }
        setIsLoading(false);
        return;
      }
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-md bg-background dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden pointer-events-auto max-h-[90vh] overflow-y-auto"
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors touch-manipulation"
                type="button"
              >
                <X className="w-4 h-4 text-muted-foreground dark:text-slate-400" />
              </button>

              {/* Header */}
              <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                <h2 className="text-2xl font-semibold text-center text-slate-900 dark:text-white">
                  Complete Your Purchase
                </h2>
              </div>

              {/* Content */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="p-6"
              >
                <div className="space-y-6">
                  {/* Pricing Info */}
                  <div className="text-center p-6 bg-gradient-to-br from-[#2B7FFF]/5 to-[#1A5FCC]/5 rounded-2xl">
                    <p className="text-sm text-muted-foreground dark:text-slate-400 mb-1">{planName}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-slate-900 dark:text-white">${normalizedAmount}</span>
                    </div>
                    <ul className="text-sm text-muted-foreground dark:text-slate-400 mt-4 space-y-1">
                      {displayedFeatures.map((feature: string) => (
                        <li key={feature}>✓ {feature}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Email Input - Only show for non-authenticated users */}
                  {!isAuthenticated ? (
                    <div className="space-y-2">
                      <Label htmlFor="checkout-email" className="text-slate-700 dark:text-slate-300">
                        Email Address
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                        <Input
                          id="checkout-email"
                          name="email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setHasAccount(false);
                            setError(null);
                          }}
                          className="h-12 pl-10 rounded-xl border-border dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-[#2B7FFF]/20 text-base"
                          disabled={isLoading}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground dark:text-slate-400">
                        We'll create your account automatically. Set your password after payment.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">
                        Account
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                        <Input
                          type="email"
                          value={email}
                          disabled
                          className="h-12 pl-10 rounded-xl border-border dark:border-slate-700 dark:bg-slate-800 dark:text-white text-base opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground dark:text-slate-400">
                        You'll be subscribed with your signed-in account.
                      </p>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm text-center">
                      {error}
                      {hasAccount && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              handleClose();
                              // Dispatch custom event to open auth modal
                              window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { mode: 'login' } }));
                            }}
                            className="text-[#2B7FFF] dark:text-[#5B9FFF] font-medium hover:underline"
                          >
                            Click here to sign in
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pay Button */}
                  <Button
                    type="button"
                    onClick={initializeCheckout}
                    disabled={isLoading || (!isAuthenticated && !email)}
                    className="w-full h-14 bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] hover:from-[#1A5FCC] hover:to-[#2B7FFF] text-white rounded-xl text-lg font-semibold shadow-lg shadow-[#2B7FFF]/25 transition-all duration-300 touch-manipulation"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Redirecting to Secure Checkout...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pay ${normalizedAmount.toFixed(2)} Securely
                      </>
                    )}
                  </Button>

                  {/* Security Note */}
                  <p className="text-center text-xs text-slate-400 dark:text-muted-foreground flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure payment powered by Stripe. PCI compliant.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

